import { AnyFilterItem, TimeShown } from '../reducers/feed.reducer';
import {
  ContentFilters,
  Dimension,
  DuplicateObjectErrorItem,
  Feed,
  FeedAllowedViewsValues,
  Filter,
  IncludeExclude,
} from '../models/core.models';
import {
  FeedPostViewMode,
  FilterItemResponse,
} from 'src/app/api/services/feed-api.service';
import { DimensionJoinTypeId, SortDirection } from '../constants';
import { createAction, props } from '@ngrx/store';

import { Alert } from '../models/alert.models';

/**
 * Broadcast by `feed.component` when a fresh result set lands, carrying the id
 * of the first feed-matching entry: the details pane's default selection. The
 * pane fetches that post's full record itself.
 */
export const feedPostsLoaded = createAction(
  '[Feed] Feed Posts Loaded',
  props<{ postId: string | null }>(),
);

export const loadFeedFilters = createAction(
  '[Feed] Load Feed Filters',
  props<{ feedId: string }>(),
);

export const loadFeedFiltersSuccess = createAction(
  '[Feed] Load Feed Filters Success',
  props<{ filters: Filter[] }>(),
);

export const loadFeedFiltersFail = createAction(
  '[Feed] Load Feed Filters Fail',
  props<{ error: any }>(),
);

/** Dispatched by `FeedResolver` after the feed loads; four consumers ride it. */
export const loadFeedSuccess = createAction(
  '[Feed] Load Feed Success',
  props<{ feed: Feed; setCurrentFeed: boolean }>(),
);
/**
 * Load filter items
 */
export const loadFilter = createAction(
  '[Filter] Load Filter',
  props<{
    feedId: string;
    filter: Filter;
    contentFilters: ContentFilters;
    offset: number;
  }>(),
);
/**
 * Load filter item success
 */
export const loadFilterSuccess = createAction(
  '[Filter] Load Filter Success',
  props<{ filterResponse: FilterItemResponse }>(),
);
/**
 * Carries the filter because there is no response payload to match the
 * spinner-clearing reducer handler against.
 */
export const loadFilterFail = createAction(
  '[Filter] Load Filter Fail',
  props<{ filter: Filter; error: any }>(),
);

export const toggleFilter = createAction(
  '[Filter] Toggle Filter',
  props<{ filter: Filter }>(),
);

export const toggleFilterDimension = createAction(
  '[Filter] Toggle Filter Dimension',
  props<{ dimension: Dimension; activate: boolean; isPivot: boolean }>(),
);

export const selectFilterItem = createAction(
  '[Filter] Select Filter Item',
  props<{
    propertyUri: string;
    filterItem: AnyFilterItem;
    mode: IncludeExclude;
  }>(),
);

export const deselectFilterItem = createAction(
  '[Filter] Deselect Filter Item',
  props<{
    propertyUri: string;
    filterItem: AnyFilterItem;
    mode: IncludeExclude;
  }>(),
);

// Fired when we have resolved title/filter_title (either from store or backend)
export const selectFilterItemResolved = createAction(
  '[Feed] Select Filter Item Resolved',
  // titleResolved: the title came from a loaded facet item or the backend; a
  // fallback title never overwrites the title of an already selected chip.
  props<{ item: AnyFilterItem; titleResolved?: boolean }>(),
);

export const selectFilterItemResolveFailed = createAction(
  '[Feed] Select Filter Item Resolve Failed',
  props<{ error: any }>(),
);

export const deactivateFilterItem = createAction(
  '[Filter] Deactivate Filter Item',
  props<{
    propertyUri: string;
    filterItem: AnyFilterItem;
  }>(),
);

export const updateActiveFilterItem = createAction(
  '[Filter] Update Active Filter Item',
  props<{
    propertyUri: string;
    filterItem: AnyFilterItem;
    mode: IncludeExclude;
  }>(),
);

export const activateFilters = createAction('[Filter] Activate Filters');

export const clearFilters = createAction('[Filter] Clear Filters');

export const pageChanged = createAction(
  '[Feed] Page Changed',
  props<{ pageNumber: number }>(),
);

export const itemsPerPageChanged = createAction(
  '[Feed] Items Per Page Changed',
  props<{ itemsPerPage: number }>(),
);

export const sortChanged = createAction(
  '[Feed] Sort Changed',
  props<{ sortField: string }>(),
);

export const sortDirectionChanged = createAction(
  '[Feed] Sort Direction Changed',
  props<{ sortDirection: SortDirection }>(),
);

export const feedUnloaded = createAction('[Feed] Feed Unloaded');

// ! ─── A feed's attached dimensions ─────────────────────────────────────────
// ! `FeedDimensionsService` owns the requests. These `…Success` broadcasts are
// ! what `feed.reducer` (dimensions + query), `filtersChanged$` and the query
// ! editor's close listener run on.

export const feedAttachDimensionsSuccess = createAction(
  '[Feed] Feed Attach Dimensions Success',
  props<{ dimensions: Array<Dimension>; query: string }>(),
);

export const feedDetachDimensionSuccess = createAction(
  '[Feed] Feed Detach Dimension Success',
  props<{ dimensionId: string; query: string }>(),
);

export const setDimensionJoinTypeSuccess = createAction(
  '[Feed] Set Feed Dimension Join Type Success',
  props<{
    dimensionId: string;
    joinTypeId: DimensionJoinTypeId;
    query: string;
  }>(),
);

export const changeWidgetsLanaguage = createAction(
  '[Feed] Change Feed Language',
  props<{ languageId: number }>(),
);

/**
 * Broadcast only — `FeedPersistenceService.edit` owns the validate→PATCH chain.
 * Six consumers across three slices, a cache patch and three `ofType` listeners
 * run on this, which is why it stays.
 */
export const editFeedSuccess = createAction(
  '[Feed] Edit Feed Success',
  props<{ feedId: string; feed: Feed }>(),
);

export const editFeedFail = createAction(
  '[Feed] Edit Feed Fail',
  props<{ error: any }>(),
);

/**
 * Broadcast only — `FeedDimensionsService.createAndAttach` owns the two-request
 * chain. `filtersChanged$` and admin's `advanced-search` listen.
 */
export const createAndAttachDimensionSuccess = createAction(
  '[Feed] Create And Attach Dimension Success',
);

export const createFeed = createAction(
  '[Feed] Create Feed',
  props<{ feedData: any }>(),
);

export const createFeedSuccess = createAction(
  '[Feed] Create Feed Success',
  props<{ feed: Feed }>(),
);

export const createFeedFail = createAction(
  '[Feed] Create Feed Fail',
  props<{ error: any }>(),
);

/**
 * The counter half of select-all: `FeedPostsService.setAllSelected` flips the
 * flags in the query cache and answers the net change, which is what
 * `feed.count_selected_posts` — a feed-wide, server-seeded count — moves by.
 */
export const selectAllPosts = createAction(
  '[Feed] Select All Posts',
  props<{ feedId: string; selected: boolean; delta: number }>(),
);

/**
 * Client state only — its reducer handler deselects every loaded post and zeroes
 * `count_selected_posts`. The POST is `injectClearFeedSelectionMutation`.
 */
export const clearFeedSelectedItems = createAction(
  '[Feed] Clean Feed Selected Items',
  props<{ feedId: string }>(),
);

export const searchPosts = createAction(
  '[Feed] Search Posts',
  props<{ query: string }>(),
);

// ! `FeedPersistenceService.duplicate` owns the request and the
// ! workspace-objects cache append; `…ConstraintError` drives the info-dialog
// ! effect.

export const duplicateFeedsFail = createAction(
  '[Feed] Duplicate Feeds Fail',
  props<{ error: any }>(),
);

export const duplicateFeedsConstraintError = createAction(
  '[Feed] Duplicate Feeds Constraint Error',
  props<{ errors: DuplicateObjectErrorItem[] }>(),
);

export const setFeedEditMode = createAction(
  '[Feed] Set Feed Edit Mode',
  props<{ editMode: boolean }>(),
);

export const toggleFeedEditMode = createAction('[Feed] Toggle Feed Edit Mode');

/**
 * Broadcast only — `filtersChanged$`'s reload cue **and** the reducer's
 * `count_selected_posts` reset.
 */
export const removeSelectedPostsFromFeedSuccess = createAction(
  '[Feed] Remove Selected Posts From Feed Success',
);

// * Broadcasts only: the alert writes are mutations, and these two exist to keep
// * `FeedState.alertsCount` in step.

export const createAlertSuccess = createAction(
  '[Feed] Create Alert Success',
  props<{ alert: Alert }>(),
);

export const deleteAlertSuccess = createAction(
  '[Feed] Delete Alert Success',
  props<{ alertId: string }>(),
);

/**
 * Client state only — its reducer handler pushes/splices `feed.views`, and
 * `FeedViewsService.toggleView` reads that **already-updated** array back to
 * build the PATCH. Dispatch order matters here.
 */
export const toggleFeedView = createAction(
  '[Feed] Toggle Feed View',
  props<{ feedId: string; view: FeedAllowedViewsValues; selected: boolean }>(),
);

/**
 * Broadcast only — the reducer patches `feed.view_posts_mode` off it.
 * `injectSetFeedPostViewModeMutation` owns the PATCH.
 */
export const setFeedPostViewModeSuccess = createAction(
  '[Feed] Set Feed Post View Mode Success',
  props<{ viewMode: FeedPostViewMode }>(),
);

export const setTimeShownOption = createAction(
  '[Feed] Set Feed Time Shown Option',
  props<{ option: TimeShown }>(),
);

export const showSelectedPostsOnly = createAction(
  '[Feed] Show Selected Posts Only',
  props<{ feedId: string; showSelectedOnly: boolean }>(),
);

export const toggleAdvancedSearch = createAction(
  '[Feed] Toggle Advanced Search',
);

// * Feed algorithms have no actions: the list is a query and detach / params /
// * apply are mutations, all behind `FeedAlgorithmsService`.
